import type { LensCalendar, LensCardType, LensComputedTemplate, LensValueFormat } from '~/types/leLens';

import type { Shaped } from '../cardView';
import { dateColumn, inferDimensions, looksLikeDate, numericColumns } from '../cardView';
import { LENS_DEFAULT_CALENDAR } from '../lensCalendar';
import { formatLensValue, isNumeric } from '../lensChart';
import { parseGrain } from '../lensGrain';
import {
  SUB_DAY_UNITS,
  type TimeScale,
  type TimeUnit,
  instantOf,
  intervalLabel,
  pointLabel,
  shift,
  timeScaleOf,
  truncate,
} from '../lensTime';
import { isPerUnitColumn } from '../ratioGuard';

/**
 * A card's reading, derived from the rows the card is already drawing.
 *
 * Nothing here fetches, and nothing here calls a model. Every sentence is
 * arithmetic over `shaped` (and, where the template needs it, the comparison
 * period's own shaped rows), which is what lets a reading recalculate the
 * instant a cross-filter moves and makes it impossible for the words to
 * disagree with the chart above them.
 *
 * `refused` is a first-class outcome rather than a fallback sentence. A
 * reading that cannot be derived honestly says which fact is missing, because
 * a hedge under a number reads as a finding and a blank card face reads as a
 * bug.
 */
export type ComputedReading =
  | { kind: 'reading'; template: LensComputedTemplate; text: string }
  | { kind: 'refused'; reason: string };

export type ComputedReadingInput = {
  type: LensCardType;
  /** Omitted resolves from the card type. */
  template?: LensComputedTemplate;
  shaped: Shaped;
  /** The same view evaluated over the comparison window, when one is active. */
  comparison?: { shaped: Shaped; label: string };
  format?: LensValueFormat;
  /**
   * The format the card itself prints a given column in. A chart resolves its
   * format per column rather than once per card, so a reading has to ask the
   * same way or it prints a bare number under an axis reading dollars.
   */
  formatFor?: (column: string) => LensValueFormat | undefined;
  /** The card's declared x, preferred over inferring one from the rows. */
  dimension?: string;
  /** The card's declared measure, preferred over inferring one from the rows. */
  measure?: string;
  /** The card's declared series split, so a rate is read per series rather than totalled across them. */
  series?: string;
  /** LensCardData.truncated: a capped result cannot be totalled or ranked. */
  truncated?: boolean;
  /** A ratio measure is never summed, so every total-shaped template refuses. */
  isRatio?: boolean;
  /**
   * The card's view declares a limit, so its rows are a deliberate top-N slice
   * rather than the whole result. Reading across them is fine; totalling them
   * is not, for the same reason a capped result cannot be totalled.
   */
  limited?: boolean;
  /**
   * A metric card's own trend, the series its sparkline is drawn from.
   *
   * A metric's `shaped` rows are already reduced to the single headline value,
   * so there is no series left in them to read. This is the same rows one level
   * finer, which the card has computed anyway.
   */
  trend?: { dates: Array<string>; values: Array<number> };
  /** The resolved, inclusive card window, needed to judge monthly bucket coverage. */
  dateRange?: { from?: string; to?: string };
  calendar?: LensCalendar;
};

/** Below this a distribution is a handful of values, not a shape worth reading. */
const MIN_DISTRIBUTION_ROWS = 4;

/** Two points are a comparison, not a trajectory. */
const MIN_TRAJECTORY_POINTS = 3;

/** How many leaders a concentration reading names. */
const CONCENTRATION_TOP_N = 3;

/** Below this a move is noise the reader should not be pointed at. */
const UNCHANGED_PERCENT = 0.05;

/** Outliers are counted past this many standard deviations from the mean. */
const OUTLIER_SIGMA = 3;

const TEMPLATE_BY_TYPE: Partial<Record<LensCardType, LensComputedTemplate>> = {
  metric: 'delta',
  line: 'trajectory',
  area: 'trajectory',
  combo: 'trajectory',
  bar: 'contribution',
  stacked: 'contribution',
  drivers: 'contribution',
  pie: 'concentration',
  scatter: 'spread',
  heatmap: 'spread',
  boxplot: 'spread',
};

/** Templates whose sentence rests on a total, and so cannot read a capped result. */
const TOTALLING_TEMPLATES: ReadonlySet<LensComputedTemplate> = new Set<LensComputedTemplate>([
  'delta',
  'contribution',
  'concentration',
]);

export function defaultTemplate(type: LensCardType): LensComputedTemplate | undefined {
  return TEMPLATE_BY_TYPE[type];
}

/**
 * The reading for one card, or undefined when the card has nothing to say.
 *
 * Undefined and `refused` mean different things and the caller must keep them
 * apart. Undefined is "this template does not apply here": no comparison is
 * set, the card type carries no reading, the rows are too few. Nothing is
 * rendered, because there is nothing a reader is missing. `refused` is "the
 * reading applies and the data blocked it", which is worth a line, because
 * otherwise one bare card among several reads as a failure.
 */
export function computeReading(input: ComputedReadingInput): ComputedReading | undefined {
  const rows = input.shaped.rows ?? [];
  const columns = input.shaped.columns ?? [];
  if (!rows.length || !columns.length) return undefined;

  const template = resolveTemplate(input, rows, columns);
  if (!template) return undefined;

  if (input.isRatio && TOTALLING_TEMPLATES.has(template)) {
    // A rate must not be added across categories, but that does not make the
    // chart unreadable. Rank the rates the reader can see instead of exposing
    // an implementation refusal on the card face.
    return readRatioRanking(input, rows, columns);
  }

  if (input.truncated && TOTALLING_TEMPLATES.has(template)) {
    return { kind: 'refused', reason: 'The result was capped before it finished, so a total would understate it.' };
  }

  switch (template) {
    case 'delta':
      return readDelta(input, rows, columns);
    case 'trajectory':
      return readTrajectory(input, rows, columns);
    case 'contribution':
      return readContribution(input, rows, columns);
    case 'concentration':
      return readConcentration(input, rows, columns);
    case 'spread':
      return readSpread(input, rows, columns);
    default:
      return undefined;
  }
}

function readRatioRanking(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): ComputedReading | undefined {
  const measure = pickMeasure(input, rows, columns);
  const dimension = pickDimension(input, rows, columns, measure);
  if (!measure || !dimension) return readSpread(input, rows, columns);

  const points = rows.flatMap((row) => {
    if (!isNumeric(row[measure])) return [];
    const key = row[dimension];
    if (typeof key !== 'string' && typeof key !== 'number' && typeof key !== 'boolean') return [];
    const label = String(key).trim();
    return label ? [{ key: label, value: Number(row[measure]) }] : [];
  });
  if (points.length < 2 || new Set(points.map((point) => point.key)).size !== points.length) {
    return readSpread(input, rows, columns);
  }

  const ranked = [...points].sort((left, right) => right.value - left.value);
  const leader = ranked[0];
  const runnerUp = ranked[1];
  const lowest = ranked[ranked.length - 1];
  const format = measureFormat(input, measure);
  const leaderName = sentenceStart(leader.key);

  if (leader.value === lowest.value) {
    return {
      kind: 'reading',
      template: 'spread',
      text: `All ${ranked.length} ${dimensionWord(dimension)} values are ${formatLensValue(leader.value, format)}.`,
    };
  }

  return {
    kind: 'reading',
    template: 'spread',
    text: `${leaderName} leads at ${formatLensValue(leader.value, format)}, followed by ${runnerUp.key} at ${formatLensValue(
      runnerUp.value,
      format,
    )}; ${lowest.key} is lowest at ${formatLensValue(lowest.value, format)}.`,
  };
}

/**
 * The template this card is actually read by.
 *
 * A bar or stacked card keyed on a date is a time series that happens to be
 * drawn with bars, so attributing a "contribution" across its x is ranking
 * days against each other and calling one of them a driver. Read it along the
 * axis it runs on instead.
 */
function resolveTemplate(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): LensComputedTemplate | undefined {
  const declared = input.template ?? defaultTemplate(input.type);
  if (!declared) return undefined;
  if (declared !== 'contribution' && declared !== 'concentration') return declared;

  const x = input.dimension;
  const runsOnDates = x ? columns.includes(x) && looksLikeDate(rows, x) : Boolean(dateColumn(rows, columns));
  return runsOnDates ? 'trajectory' : declared;
}

function readDelta(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): ComputedReading | undefined {
  const measure = pickMeasure(input, rows, columns);
  if (!measure) return undefined;

  const current = total(rows, measure);
  if (current === undefined) return undefined;

  const format = measureFormat(input, measure);
  const currentText = formatLensValue(current, format);
  // No comparison window is set, which is the default on most boards. The
  // card's own sparkline series still carries an answer, and reading it costs
  // nothing: it is the difference between a headline that speaks on first open
  // and one that stays silent until someone turns a control on.
  if (!input.comparison) {
    const trend = input.trend;
    if (!trend) return undefined;
    return trajectoryFrom(
      trend.dates.map((at, index) => ({ at, value: trend.values[index] })).filter((point) => isNumeric(point.value)),
      format,
      input.dateRange,
      scaleOf(input, trend.dates),
    );
  }

  const priorRows = input.comparison.shaped.rows ?? [];
  const priorColumns = input.comparison.shaped.columns ?? [];
  const priorMeasure = priorColumns.includes(measure) ? measure : pickMeasure(input, priorRows, priorColumns);
  const prior = priorMeasure ? total(priorRows, priorMeasure) : undefined;

  if (prior === undefined || prior === 0) {
    return {
      kind: 'refused',
      reason: `${currentText}. ${periodPhrase(input.comparison.label, true)} has no comparable figure, so there is no change to report.`,
    };
  }

  const change = ((current - prior) / Math.abs(prior)) * 100;
  const period = periodPhrase(input.comparison.label, false);

  if (Math.abs(change) < UNCHANGED_PERCENT) {
    return { kind: 'reading', template: 'delta', text: `${currentText}, unchanged on ${period}.` };
  }

  return {
    kind: 'reading',
    template: 'delta',
    text: `${currentText}, ${change > 0 ? 'up' : 'down'} ${percent(Math.abs(change))} on ${period}.`,
  };
}

function readTrajectory(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): ComputedReading | undefined {
  const measure = pickMeasure(input, rows, columns);
  const dates = dateColumn(rows, columns);
  if (!measure || !dates || dates === measure) return undefined;

  if (
    (isRateMeasure(input, measure) || (input.series && columns.includes(input.series))) &&
    rows.length > new Set(rows.map((row) => row[dates])).size
  ) {
    return readSeriesTrajectory(input, rows, columns, measure, dates);
  }

  // A card with a series carries several rows per date, so the series has to be
  // totalled back to one point before it can be read along its axis. Reading
  // the raw rows instead compares one supplier's Tuesday with another's Friday.
  const byDate = totalsByKey(rows, dates, measure);
  const points = [...byDate.entries()]
    .map(([at, value]) => ({ at, value }))
    .sort((left, right) => sortableDate(left.at) - sortableDate(right.at));

  // Totalling a deliberate top-N slice understates every point on it, which is
  // the same objection that stops a capped result being totalled.
  if (input.limited && points.length < rows.length) {
    return {
      kind: 'refused',
      reason: 'This card shows a ranked slice rather than every row, so a total per date would understate it.',
    };
  }

  return trajectoryFrom(
    points,
    measureFormat(input, measure),
    input.dateRange,
    scaleOf(
      input,
      points.map((point) => point.at),
      dates,
    ),
  );
}

function isRateMeasure(input: ComputedReadingInput, measure: string): boolean {
  return Boolean(input.isRatio) || measureFormat(input, measure)?.kind === 'percent' || isPerUnitColumn(measure);
}

function readSeriesTrajectory(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  measure: string,
  dates: string,
): ComputedReading | undefined {
  const series =
    input.series && columns.includes(input.series)
      ? input.series
      : inferDimensions(rows, columns).find((column) => column !== dates && column !== measure);
  if (!series) {
    return {
      kind: 'refused',
      reason: `${measure} is a rate, so it cannot be added across the rows that share a date.`,
    };
  }

  const format = measureFormat(input, measure);
  const paths = new Map<string, Array<{ at: string; value: number }>>();
  for (const row of rows) {
    const key = row[series];
    if (!isNumeric(row[measure]) || (typeof key !== 'string' && typeof key !== 'number' && typeof key !== 'boolean'))
      continue;
    const label = String(key).trim();
    if (!label) continue;
    paths.set(label, [...(paths.get(label) ?? []), { at: String(row[dates]), value: Number(row[measure]) }]);
  }
  const trajectories = [...paths.entries()]
    .map(([label, points]) => ({
      label,
      points: points.sort((left, right) => sortableDate(left.at) - sortableDate(right.at)),
    }))
    .filter(({ points }) => points.length >= MIN_TRAJECTORY_POINTS)
    .map(({ label, points }) => ({ label, points, first: points[0], last: points[points.length - 1] }));
  if (!trajectories.length) return undefined;
  const scale = scaleOf(
    input,
    trajectories.flatMap(({ points }) => points.map((point) => point.at)),
    dates,
  );
  if (trajectories.length === 1)
    return trajectoryFrom(paths.get(trajectories[0].label)!, format, input.dateRange, scale);

  const leader = trajectories.reduce((best, entry) => (entry.last.value > best.last.value ? entry : best));
  const mover = trajectories.reduce((best, entry) =>
    Math.abs(entry.last.value - entry.first.value) > Math.abs(best.last.value - best.first.value) ? entry : best,
  );
  const opening = `${sentenceStart(leader.label)} ended highest at ${formatLensValue(leader.last.value, format)} ${when(leader.last.at, scale)}.`;
  const partial = trajectories
    .map((trajectory) => ({ trajectory, coverage: bucketCoverage(trajectory.points, input.dateRange, scale) }))
    .find(({ coverage }) => coverage && !coverage.complete);
  if (partial?.coverage) {
    return {
      kind: 'reading',
      template: 'trajectory',
      text: `${sentenceStart(leader.label)} is ${formatLensValue(leader.last.value, format)} ${when(leader.last.at, scale)}. ${sentenceStart(partial.trajectory.label)}: ${bucketCoverageText(partial.coverage, partial.trajectory.first.at, partial.trajectory.last.at, scale)} No series movement is ranked when ${BUCKET_ADJECTIVE[scale?.unit ?? 'month']} coverage is partial or unverified.`,
    };
  }
  // Explicit series can be overlapping comparison periods, rather than parts
  // of one total. Keep their ranges separate; axis dates may also align the
  // prior period onto the current window instead of naming its actual dates.
  if (!isRateMeasure(input, measure)) {
    const ranked = [...trajectories].sort(
      (left, right) =>
        Math.max(...right.points.map(({ value }) => Math.abs(value))) -
        Math.max(...left.points.map(({ value }) => Math.abs(value))),
    );
    const ranges = ranked.slice(0, 3).map(({ label, points }) => {
      const values = points.map(({ value }) => value);
      return `${sentenceStart(label)} ranges from ${formatLensValue(Math.min(...values), format)} to ${formatLensValue(Math.max(...values), format)}.`;
    });
    if (trajectories.length > 3)
      ranges.push(`The query result includes ${trajectories.length - 3} additional series; the chart may group them.`);
    return { kind: 'reading', template: 'trajectory', text: ranges.join(' ') };
  }
  const move = mover.last.value - mover.first.value;
  if (move === 0) return { kind: 'reading', template: 'trajectory', text: opening };
  return {
    kind: 'reading',
    template: 'trajectory',
    text: `${opening} ${sentenceStart(mover.label)} moved most, ${move > 0 ? 'up' : 'down'} from ${formatLensValue(
      mover.first.value,
      format,
    )} ${when(mover.first.at, scale)} to ${formatLensValue(mover.last.value, format)} ${when(mover.last.at, scale)}.`,
  };
}

function trajectoryFrom(
  points: Array<{ at: string; value: number }>,
  format: LensValueFormat | undefined,
  dateRange?: { from?: string; to?: string },
  scale?: TimeScale,
): ComputedReading | undefined {
  if (points.length < MIN_TRAJECTORY_POINTS) return undefined;

  const first = points[0];
  const last = points[points.length - 1];
  const firstText = formatLensValue(first.value, format);
  const lastText = formatLensValue(last.value, format);

  const coverage = bucketCoverage(points, dateRange, scale);
  if (coverage && !coverage.complete) {
    const firstBucket = readableBucket(first.at, scale);
    const lastBucket = readableBucket(last.at, scale);
    const unit = scale?.unit ?? 'month';
    return {
      kind: 'reading',
      template: 'trajectory',
      text: `${firstBucket}: ${firstText}; ${lastBucket}: ${lastText}. ${bucketCoverageText(coverage, first.at, last.at, scale)} No ${unit}-to-${unit} movement is reported for partial or unverified coverage.`,
    };
  }

  let biggest = { at: points[1].at, move: points[1].value - points[0].value };
  for (let index = 1; index < points.length; index += 1) {
    const move = points[index].value - points[index - 1].value;
    if (Math.abs(move) > Math.abs(biggest.move)) {
      biggest = { at: points[index].at, move };
    }
  }

  const direction = last.value > first.value ? 'Rose' : last.value < first.value ? 'Fell' : 'Held level';
  // Both endpoints are dated, because a chart may draw only its top-ranked
  // points while the rows behind it hold every one. Without the dates a reader
  // compares the sentence to the bars and cannot find the numbers in it.
  const opening =
    direction === 'Held level'
      ? biggest.move === 0
        ? `Held near ${lastText} from ${readableDate(first.at, scale)} to ${readableDate(last.at, scale)}.`
        : `Started at ${firstText} ${when(first.at, scale)} and ended at ${lastText} ${when(last.at, scale)}.`
      : `${direction} from ${firstText} ${when(first.at, scale)} to ${lastText} ${when(last.at, scale)}.`;

  if (biggest.move === 0) {
    return { kind: 'reading', template: 'trajectory', text: opening };
  }

  const moveText = formatLensValue(Math.abs(biggest.move), format);
  return {
    kind: 'reading',
    template: 'trajectory',
    text: `${opening} The largest single move was ${when(biggest.at, scale)}, ${
      biggest.move > 0 ? 'up' : 'down'
    } ${moveText} on the point before it.`,
  };
}

function bucketCoverage(
  points: Array<{ at: string; value: number }>,
  range?: { from?: string; to?: string },
  scale?: TimeScale,
): {
  complete: boolean;
  firstDays?: number;
  firstBucketDays?: number;
  lastDays?: number;
  lastBucketDays?: number;
} | null {
  if (!scale || !COVERAGE_UNITS.includes(scale.unit)) return null;
  const { unit, calendar } = scale;
  const starts = points.map((point) => instantOf(point.at, calendar));
  if (starts.some((start) => start === undefined || truncate(start, unit, calendar) !== start)) return null;
  const buckets = starts as Array<number>;
  if (buckets.some((start, index) => index > 0 && start !== shift(buckets[index - 1], unit, 1, calendar))) {
    return { complete: false };
  }

  const from = range?.from ? instantOf(range.from, calendar) : undefined;
  const through = range?.to ? instantOf(range.to, calendar) : undefined;
  if (from === undefined || through === undefined || from > through) return { complete: false };
  const to = shift(through, 'day', 1, calendar);

  const covered = buckets.map((start) => {
    const end = shift(start, unit, 1, calendar);
    return { selectedDays: daysBetween(Math.max(from, start), Math.min(to, end)), bucketDays: daysBetween(start, end) };
  });
  return {
    complete: covered.every(({ selectedDays, bucketDays }) => selectedDays === bucketDays),
    firstDays: covered[0].selectedDays,
    firstBucketDays: covered[0].bucketDays,
    lastDays: covered[covered.length - 1].selectedDays,
    lastBucketDays: covered[covered.length - 1].bucketDays,
  };
}

function bucketCoverageText(
  coverage: Exclude<ReturnType<typeof bucketCoverage>, null>,
  first: string,
  last: string,
  scale?: TimeScale,
): string {
  if (coverage.firstDays === undefined || coverage.lastDays === undefined) {
    return `The selected-day coverage of these ${BUCKET_ADJECTIVE[scale?.unit ?? 'month']} buckets is unavailable.`;
  }
  return `The selected window includes ${coverage.firstDays} of ${coverage.firstBucketDays} days of ${readableBucket(first, scale)} and ${coverage.lastDays} of ${coverage.lastBucketDays} days of ${readableBucket(last, scale)}.`;
}

const COVERAGE_UNITS: ReadonlyArray<TimeUnit> = ['week', 'month', 'quarter', 'year'];

const BUCKET_ADJECTIVE: Partial<Record<TimeUnit, string>> = {
  week: 'weekly',
  month: 'monthly',
  quarter: 'quarterly',
  year: 'yearly',
};

function daysBetween(start: number, end: number): number {
  return Math.max(0, Math.round((end - start) / (24 * 60 * 60 * 1000)));
}

function readableBucket(value: string, scale?: TimeScale): string {
  const calendar = scale?.calendar ?? LENS_DEFAULT_CALENDAR;
  const instant = instantOf(value, calendar);
  return instant === undefined
    ? value
    : intervalLabel(instant, scale?.unit ?? 'month', { calendar, yearInView: 'always' });
}

function readContribution(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): ComputedReading | undefined {
  const measure = pickMeasure(input, rows, columns);
  const dimension = pickDimension(input, rows, columns, measure);
  if (!measure || !dimension) return undefined;
  if (!input.comparison) return readConcentration(input, rows, columns);

  const priorRows = input.comparison.shaped.rows ?? [];
  if (!priorRows.length) return readConcentration(input, rows, columns);

  const now = totalsByKey(rows, dimension, measure);
  const prior = totalsByKey(priorRows, dimension, measure);
  if (now.size < 2) return undefined;

  let totalMove = 0;
  const moves: Array<{ key: string; move: number }> = [];
  now.forEach((value, key) => {
    const before = prior.get(key);
    if (before === undefined) return;
    const move = value - before;
    totalMove += move;
    moves.push({ key, move });
  });

  if (moves.length < 2) return undefined;

  const leader = moves.reduce((best, entry) => (Math.abs(entry.move) > Math.abs(best.move) ? entry : best));
  if (leader.move === 0) {
    return {
      kind: 'reading',
      template: 'contribution',
      text: `Every ${dimensionWord(dimension)} held against ${periodPhrase(input.comparison.label, false)}.`,
    };
  }

  const format = measureFormat(input, measure);
  const leaderText = formatLensValue(Math.abs(leader.move), format);
  const period = periodPhrase(input.comparison.label, false);

  // A total that nets to roughly nothing is the interesting case rather than a
  // failure: the parts moved and offset each other, and saying so is truer
  // than dividing by a denominator that rounds to zero.
  if (Math.abs(totalMove) < Math.abs(leader.move) * 0.05) {
    return {
      kind: 'reading',
      template: 'contribution',
      text: `The total barely moved on ${period}, but the parts did: ${leader.key} is ${
        leader.move > 0 ? 'up' : 'down'
      } ${leaderText}, offset elsewhere.`,
    };
  }

  const share = Math.min(Math.abs(leader.move) / Math.abs(totalMove), 1) * 100;
  const totalText = formatLensValue(Math.abs(totalMove), format);
  const movement = totalMove > 0 ? 'rise' : 'fall';

  if (share >= 99) {
    return {
      kind: 'reading',
      template: 'contribution',
      text: `${leader.key} carries the whole of the ${movement}: ${
        leader.move > 0 ? 'up' : 'down'
      } ${leaderText} against a ${totalText} total on ${period}.`,
    };
  }

  return {
    kind: 'reading',
    template: 'contribution',
    text: `${leader.key} carries ${percent(share, 0)} of the ${movement}: ${
      leader.move > 0 ? 'up' : 'down'
    } ${leaderText} against a ${totalText} total on ${period}.`,
  };
}

function readConcentration(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): ComputedReading | undefined {
  const measure = pickMeasure(input, rows, columns);
  const dimension = pickDimension(input, rows, columns, measure);
  if (!measure || !dimension) return undefined;

  const totals = totalsByKey(rows, dimension, measure);
  if (totals.size < MIN_TRAJECTORY_POINTS) return undefined;

  const values = [...totals.values()];
  // A share of a mixed-sign total is not a share of anything a reader can
  // picture, so this refuses rather than printing a percentage over 100.
  if (values.some((value) => value < 0)) return undefined;

  const sum = values.reduce((running, value) => running + value, 0);
  if (sum <= 0) return undefined;

  const ranked = [...totals.entries()].sort((left, right) => right[1] - left[1]);
  const top = ranked.slice(0, CONCENTRATION_TOP_N);
  const share = (top.reduce((running, [, value]) => running + value, 0) / sum) * 100;

  if (top.length === 1) {
    return {
      kind: 'reading',
      template: 'concentration',
      text: `${top[0][0]} is ${percent(share, 0)} of the total.`,
    };
  }

  return {
    kind: 'reading',
    template: 'concentration',
    text: `The top ${top.length} of ${ranked.length} carry ${percent(share, 0)} of the total, led by ${top[0][0]}.`,
  };
}

function readSpread(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): ComputedReading | undefined {
  const measure = pickMeasure(input, rows, columns);
  if (!measure) return undefined;

  const format = measureFormat(input, measure);
  const values = rows.filter((row) => isNumeric(row[measure])).map((row) => Number(row[measure]));
  if (values.length < MIN_DISTRIBUTION_ROWS) return undefined;

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    return {
      kind: 'reading',
      template: 'spread',
      text: `Every one of the ${values.length} values is ${formatLensValue(min, format)}.`,
    };
  }

  const mean = values.reduce((running, value) => running + value, 0) / values.length;
  const variance = values.reduce((running, value) => running + (value - mean) ** 2, 0) / values.length;
  const deviation = Math.sqrt(variance);
  const opening = `Ranges ${formatLensValue(min, format)} to ${formatLensValue(max, format)}.`;

  if (deviation === 0) {
    return { kind: 'reading', template: 'spread', text: opening };
  }

  const outliers = values.filter((value) => Math.abs(value - mean) > OUTLIER_SIGMA * deviation).length;
  if (!outliers) {
    return {
      kind: 'reading',
      template: 'spread',
      text: `${opening} Nothing sits more than ${OUTLIER_SIGMA} standard deviations from the mean of ${formatLensValue(
        mean,
        format,
      )}.`,
    };
  }

  return {
    kind: 'reading',
    template: 'spread',
    text: `${opening} ${outliers} of ${values.length} values sit more than ${OUTLIER_SIGMA} standard deviations from the mean of ${formatLensValue(
      mean,
      format,
    )}.`,
  };
}

/** The card's own format for this measure, since a chart resolves format per column. */
function measureFormat(input: ComputedReadingInput, measure: string): LensValueFormat | undefined {
  return input.format ?? input.formatFor?.(measure);
}

function pickMeasure(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
): string | undefined {
  if (input.measure && columns.includes(input.measure)) return input.measure;
  const numeric = numericColumns(rows, columns);
  if (!numeric.length) return undefined;
  // More than one measure and no declared one has no single honest reduction,
  // the same rule resolveMetricHeadline follows for a metric card.
  return numeric.length === 1 ? numeric[0] : undefined;
}

function pickDimension(
  input: ComputedReadingInput,
  rows: Array<Record<string, unknown>>,
  columns: Array<string>,
  measure: string | undefined,
): string | undefined {
  if (
    input.dimension &&
    columns.includes(input.dimension) &&
    input.dimension !== measure &&
    !looksLikeDate(rows, input.dimension)
  ) {
    return input.dimension;
  }
  return inferDimensions(rows, columns).find((column) => column !== measure);
}

function total(rows: Array<Record<string, unknown>>, measure: string): number | undefined {
  let sum = 0;
  let seen = false;
  for (const row of rows) {
    if (!isNumeric(row[measure])) continue;
    sum += Number(row[measure]);
    seen = true;
  }
  return seen ? sum : undefined;
}

function totalsByKey(rows: Array<Record<string, unknown>>, dimension: string, measure: string): Map<string, number> {
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (!isNumeric(row[measure])) continue;
    const key = row[dimension];
    // Only a primitive names a category. An object would stringify to
    // "[object Object]" and quietly collapse every distinct value into one.
    if (typeof key !== 'string' && typeof key !== 'number' && typeof key !== 'boolean') continue;
    const label = String(key);
    if (!label) continue;
    totals.set(label, (totals.get(label) ?? 0) + Number(row[measure]));
  }
  return totals;
}

/**
 * The comparison control's own label, turned into something a sentence can
 * end on. `capitalised` is for the one refusal that opens on it.
 */
function periodPhrase(label: string, capitalised: boolean): string {
  const lower = label.trim().toLowerCase();
  const phrase = lower.startsWith('same period') ? lower : `the ${lower}`;
  return capitalised ? `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}` : phrase;
}

function percent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

function sortableDate(value: string): number {
  return instantOf(value) ?? Number.POSITIVE_INFINITY;
}

/** Dates print as the card's own axis prints them, not as an ISO timestamp. */
function readableDate(value: string, scale?: TimeScale): string {
  const instant = scale ? instantOf(value, scale.calendar) : undefined;
  return scale && instant !== undefined ? pointLabel(instant, scale) : value;
}

function when(value: string, scale?: TimeScale): string {
  const instant = scale ? instantOf(value, scale.calendar) : undefined;
  if (!scale || instant === undefined) {
    return `on ${value}`;
  }
  if (SUB_DAY_UNITS.includes(scale.unit)) {
    return `at ${intervalLabel(instant, scale.unit, scale)} on ${intervalLabel(instant, 'day', scale)}`;
  }
  const label = intervalLabel(instant, scale.unit, scale);
  return scale.unit === 'day' ? `on ${label}` : scale.unit === 'week' ? `in the week of ${label}` : `in ${label}`;
}

function scaleOf(input: ComputedReadingInput, values: ReadonlyArray<string>, column = ''): TimeScale | undefined {
  return timeScaleOf(values, input.calendar ?? LENS_DEFAULT_CALENDAR, column, parseGrain(column)?.grain)?.scale;
}

/** "product" out of "product_bk", so a sentence names the split in the reader's words. */
function dimensionWord(column: string): string {
  const cleaned = column
    .replace(/_(bk|id|key|name)$/i, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return cleaned || 'group';
}

function sentenceStart(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
